import type { FiltrosDeChamado } from '../../../api/filtrosDeChamado'
import { ROTULO_SITUACAO_SLA, TERMOS, TEXTOS } from '../../../lib/glossario'
import type { OrigemDaDemanda, SituacaoDeSla } from '../../../types/chamado'

/**
 * Os atalhos de triagem: os recortes que quase todo mundo faz, a um clique.
 *
 * Cada atalho escreve os MESMOS filtros do painel — não existe uma segunda consulta, nem parâmetro
 * que só o chip conheça. É por isso que o chip aceso e o painel aberto nunca podem discordar. No
 * sistema anterior a listagem, o painel e a exportação tinham cada um o seu jeito de dizer "fora do
 * prazo", e as três telas mostravam contagens diferentes para a mesma pergunta.
 *
 * O que o atalho NÃO toca é o resto do recorte — competência, grupo, entidade, período e busca.
 * Quem escolheu maio e depois clica em "Prazo estourado" quer as duas coisas, não perder o mês.
 */

/** A parte dos filtros que um atalho escreve. */
export interface Recorte {
  situacaoSla: SituacaoDeSla[]
  origemDemanda: OrigemDaDemanda[]
  /** `false` é a fila de julgamento; `null` é "tanto faz", que é o que os outros atalhos dizem. */
  julgado: boolean | null
}

export interface Triagem {
  /** Identificador estável. Não é rótulo: trocar o texto do chip não pode trocar a chave. */
  id: string
  rotulo: string
  /** Uma linha explicando o recorte, para quem não é de TI. Vira o `title` do chip. */
  ajuda: string
  recorte: Recorte
}

const TODOS: Recorte = { situacaoSla: [], origemDemanda: [], julgado: null }

export const TRIAGENS: Triagem[] = [
  {
    id: 'todos',
    rotulo: 'Todos',
    ajuda: 'Todos os chamados carregados que atendem aos demais filtros.',
    recorte: TODOS,
  },
  {
    id: 'no-prazo',
    rotulo: ROTULO_SITUACAO_SLA.DENTRO_DO_PRAZO ?? 'Dentro do prazo',
    ajuda: 'Chamados atendidos até o prazo combinado, ou ainda abertos e dentro dele.',
    recorte: { ...TODOS, situacaoSla: ['DENTRO_DO_PRAZO'] },
  },
  {
    id: 'estourado',
    rotulo: ROTULO_SITUACAO_SLA.ESTOURADO ?? 'Prazo estourado',
    ajuda: 'Chamados fechados depois do prazo, ou ainda abertos com o prazo já vencido.',
    recorte: { ...TODOS, situacaoSla: ['ESTOURADO'] },
  },
  {
    // O terceiro chip existe pelo mesmo motivo que o estado existe no domínio: sem ele, "sem prazo"
    // só apareceria misturado em "Todos", e quem faz triagem por prazo nunca veria que um sétimo da
    // lista está fora da medição. O texto de ajuda é o do glossário, inteiro: é a explicação que
    // impede a leitura de que estes chamados foram atendidos em dia.
    id: 'sem-prazo',
    rotulo: ROTULO_SITUACAO_SLA.SEM_PRAZO ?? 'Sem prazo definido',
    ajuda: TERMOS.semPrazoDeSla.ajuda,
    recorte: { ...TODOS, situacaoSla: ['SEM_PRAZO'] },
  },
  {
    id: 'projetos',
    rotulo: 'Projetos',
    ajuda: TERMOS.origemDaDemanda.ajuda,
    recorte: { ...TODOS, origemDemanda: ['PROJETO'] },
  },
  {
    // O sexto atalho é o único que existe por causa de TRABALHO, e não de leitura: agosto/2026 fecha
    // com 107 chamados e zero julgamentos, e o fechamento recusa enquanto sobrar um. Sem ele, a
    // única forma de saber o que já foi julgado era abrir os 107 — e reabrir os já feitos.
    id: 'sem-julgamento',
    rotulo: TEXTOS.chamados.semJulgamento,
    ajuda: TEXTOS.chamados.semJulgamentoAjuda,
    recorte: { ...TODOS, julgado: false },
  },
]

/**
 * Qual atalho corresponde ao recorte que está na URL agora.
 *
 * Exige correspondência EXATA nos três critérios. Um filtro montado à mão no painel não acende chip
 * nenhum, e é isso que se quer: acender "Todos" numa lista que está mostrando só os projetos faria o
 * chip afirmar um recorte que não é o que está na tela.
 *
 * @param filtros critérios correntes
 * @returns o id do atalho correspondente, ou nulo quando o recorte é outro
 */
export function triagemAtiva(filtros: FiltrosDeChamado): string | null {
  const encontrada = TRIAGENS.find(
    ({ recorte }) =>
      mesmoConjunto(recorte.situacaoSla, filtros.situacaoSla) &&
      mesmoConjunto(recorte.origemDemanda, filtros.origemDemanda) &&
      recorte.julgado === filtros.julgado,
  )
  return encontrada?.id ?? null
}

/** Mesma coleção, independentemente da ordem em que os parâmetros vieram na URL. */
function mesmoConjunto(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((valor) => b.includes(valor))
}
